import type { CategorySales } from '@/domain/entities/category-sales.entity'
import type {
  DashboardOverview,
  DateRange,
  ProductSalesRank,
} from '@/domain/entities/dashboard-overview.entity'
import type { HourlySales } from '@/domain/entities/hourly-sales.entity'
import type { KpiMetric, MetricTrend } from '@/domain/entities/metric.entity'
import type { Order, OrderStatus, PaymentMethod } from '@/domain/entities/order.entity'
import type { OutletSales } from '@/domain/entities/outlet-sales.entity'
import type { RevenuePoint } from '@/domain/entities/revenue.entity'
import type {
  CategorySalesDto,
  DashboardOverviewDto,
  HourlySalesDto,
  KpiMetricDto,
  OrderDto,
  OutletSalesDto,
  ProductSalesRankDto,
  RevenuePointDto,
} from './dashboard.dto'

function toTrend(delta: number): MetricTrend {
  if (delta > 0.05) return 'up'
  if (delta < -0.05) return 'down'
  return 'flat'
}

/** Mapper: mengubah DTO (API) menjadi domain entity yang bersih. Defensif terhadap null/undefined. */
export const dashboardMapper = {
  toOverview(dto: DashboardOverviewDto): DashboardOverview {
    const filterDto = dto?.filter || {}
    const summaryDto = dto?.summary || {}
    const prodDto = summaryDto?.products_sold || {}

    const revTrendDtos = Array.isArray(dto?.revenue_trend) ? dto.revenue_trend : []
    const catSalesDtos = Array.isArray(dto?.category_sales) ? dto.category_sales : []
    const outletSalesDtos = Array.isArray(dto?.outlet_sales) ? dto.outlet_sales : []
    const hourlySalesDtos = Array.isArray(dto?.hourly_sales) ? dto.hourly_sales : []
    const topProdDtos = Array.isArray(dto?.top_products) ? dto.top_products : []
    const leastProdDtos = Array.isArray(dto?.least_products) ? dto.least_products : []

    const revGrowth = summaryDto.revenue_growth_percent ?? 0
    const txGrowth = summaryDto.transaction_growth_percent ?? 0
    const prodGrowth = prodDto.growth_percent ?? 0

    return {
      filter: {
        period: (filterDto.period as DateRange) || 'today',
        startDate: filterDto.start_date,
        endDate: filterDto.end_date,
        outletId: filterDto.outlet_id,
      },
      summary: {
        totalRevenue: summaryDto.total_revenue ?? 0,
        revenueGrowthPercent: revGrowth,
        revenueTrend: (summaryDto.revenue_trend as MetricTrend) || toTrend(revGrowth),
        totalTransactions: summaryDto.total_transactions ?? 0,
        transactionGrowthPercent: txGrowth,
        transactionTrend: (summaryDto.transaction_trend as MetricTrend) || toTrend(txGrowth),
        productsSold: {
          totalQty: prodDto.total_qty ?? 0,
          singleProductQty: prodDto.single_product_qty ?? 0,
          packageQty: prodDto.package_qty ?? 0,
          packageComponentQty: prodDto.package_component_qty ?? 0,
          growthPercent: prodGrowth,
          trend: (prodDto.trend as MetricTrend) || toTrend(prodGrowth),
        },
      },
      revenueTrend: revTrendDtos.map(dashboardMapper.toRevenuePoint),
      categorySales: catSalesDtos.map(dashboardMapper.toCategorySales),
      outletSales: outletSalesDtos.map(dashboardMapper.toOutletSales),
      hourlySales: hourlySalesDtos.map(dashboardMapper.toHourlySales),
      topProducts: topProdDtos.map(dashboardMapper.toProductSalesRank),
      leastProducts: leastProdDtos.map(dashboardMapper.toProductSalesRank),
    }
  },

  toMetric(dto: KpiMetricDto): KpiMetric {
    const delta = dto?.delta_percent ?? 0
    return {
      id: dto?.id || '',
      label: dto?.label || '',
      value: dto?.value ?? 0,
      format: (dto?.format as KpiMetric['format']) ?? 'number',
      deltaPercent: delta,
      trend: toTrend(delta),
    }
  },

  toRevenuePoint(dto: RevenuePointDto): RevenuePoint {
    return {
      date: dto?.date || '',
      revenue: dto?.revenue ?? 0,
      transactions: dto?.transactions ?? 0,
    }
  },

  toCategorySales(dto: CategorySalesDto): CategorySales {
    return {
      category: dto?.category_name || dto?.category || 'Tanpa Kategori',
      outletName: dto?.outlet_name || dto?.outlet || 'Semua Outlet',
      revenue: dto?.revenue ?? 0,
      share: dto?.percentage ?? dto?.share ?? 0,
    }
  },

  toOutletSales(dto: OutletSalesDto): OutletSales {
    return {
      outlet: dto?.outlet_name || dto?.outlet || 'Cabang',
      revenue: dto?.revenue ?? 0,
      transactions: dto?.transactions ?? 0,
    }
  },

  toHourlySales(dto: HourlySalesDto): HourlySales {
    const h = dto?.hour ?? 0
    return {
      hour: h,
      hourLabel: String(h).padStart(2, '0'),
      revenue: dto?.revenue ?? 0,
      transactions: dto?.transactions ?? 0,
    }
  },

  toOrder(dto: OrderDto): Order {
    return {
      id: dto?.id || '',
      invoiceNo: dto?.invoice_no || '',
      customer: dto?.customer || '',
      createdAt: dto?.created_at || '',
      itemCount: dto?.item_count ?? 0,
      total: dto?.total ?? 0,
      status: (dto?.status as OrderStatus) || 'completed',
      payment: (dto?.payment as PaymentMethod) || 'cash',
    }
  },

  toProductSalesRank(dto: ProductSalesRankDto): ProductSalesRank {
    return {
      productId: dto?.product_id || '',
      productName: dto?.product_name || 'Produk',
      outletId: dto?.outlet_id || '',
      outletName: dto?.outlet_name || 'Semua Outlet',
      sku: dto?.sku || '',
      categoryName: dto?.category_name || 'Tanpa Kategori',
      currentStock: dto?.current_stock ?? 0,
      stockActive: Boolean(dto?.stock_active),
      totalQty: dto?.total_qty ?? 0,
      revenue: dto?.revenue ?? 0,
    }
  },
}
